import { z } from 'zod';

/**
 * Provisioning: the resumable run that turns a newly created tenant into a working store
 * (TARGET_ARCHITECTURE §8.2, docs/PROVISIONING.md). Enum values are shared with the database
 * enums; a test checks that they match exactly.
 */

/**
 * The steps, in order. Each is idempotent and recorded; a failed run resumes at its step.
 * - CREATE_TENANT_RECORDS: tenant, store, system address, owner and invitation (one
 *   transaction, in the request that creates the tenant)
 * - CLONE_CATEGORIES: the master categories
 * - CLONE_PRODUCTS: the master products with their facts, price, stock row and image links, in
 *   batches with a persisted cursor
 * - OPEN_ADMIN: the tenant becomes ACTIVE, so the owner can sign in to the store admin
 * - GENERATE_CONTENT: waits until the content engine has written the store's marketing copy
 * - OPEN_STOREFRONT: the store becomes public
 */
export const PROVISIONING_STEPS = [
  'CREATE_TENANT_RECORDS',
  'CLONE_CATEGORIES',
  'CLONE_PRODUCTS',
  'OPEN_ADMIN',
  'GENERATE_CONTENT',
  'OPEN_STOREFRONT',
] as const;
export const provisioningStepSchema = z.enum(PROVISIONING_STEPS);
export type ProvisioningStep = z.infer<typeof provisioningStepSchema>;

/**
 * - PENDING: waiting for the worker
 * - RUNNING: the worker is on it (or retrying after an error)
 * - WAITING: parked on a condition outside the run (the marketing copy); not an error
 * - FAILED: out of automatic retries; a platform admin can retry it
 * - SUCCEEDED: every step is done
 */
export const PROVISIONING_RUN_STATUSES = [
  'PENDING',
  'RUNNING',
  'WAITING',
  'FAILED',
  'SUCCEEDED',
] as const;
export const provisioningRunStatusSchema = z.enum(PROVISIONING_RUN_STATUSES);
export type ProvisioningRunStatus = z.infer<typeof provisioningRunStatusSchema>;

export const PROVISIONING_STEP_STATUSES = [
  'PENDING',
  'RUNNING',
  'WAITING',
  'FAILED',
  'SUCCEEDED',
] as const;
export const provisioningStepStatusSchema = z.enum(PROVISIONING_STEP_STATUSES);
export type ProvisioningStepStatus = z.infer<typeof provisioningStepStatusSchema>;

/**
 * Why a run stopped, stored as a code (never an error message, which could carry data).
 * - UNEXPECTED: a step threw; the worker log has the details under the tenant id
 * - STALLED: the worker kept dying on this run (for example killed mid-step) until the attempt
 *   limit
 */
export const PROVISIONING_ERROR_CODES = ['UNEXPECTED', 'STALLED'] as const;
export const provisioningErrorCodeSchema = z.enum(PROVISIONING_ERROR_CODES);
export type ProvisioningErrorCode = z.infer<typeof provisioningErrorCodeSchema>;

/** Why a run is WAITING. */
export const PROVISIONING_WAIT_REASONS = [
  /** The content engine is not configured: the store stays closed until it is. */
  'CONTENT_ENGINE_UNAVAILABLE',
  /** The store's copy is being written; it opens by itself when that is done. */
  'CONTENT_IN_PROGRESS',
] as const;
export const provisioningWaitReasonSchema = z.enum(PROVISIONING_WAIT_REASONS);
export type ProvisioningWaitReason = z.infer<typeof provisioningWaitReasonSchema>;

export const provisioningStepStateSchema = z.object({
  step: provisioningStepSchema,
  status: provisioningStepStatusSchema,
  /** How many times the worker started this step. */
  attempts: z.number().int().min(0),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  errorCode: provisioningErrorCodeSchema.nullable(),
});
export type ProvisioningStepState = z.infer<typeof provisioningStepStateSchema>;

export const provisioningRunSchema = z.object({
  id: z.uuid(),
  status: provisioningRunStatusSchema,
  currentStep: provisioningStepSchema,
  /** Job executions so far; the sweeper gives up at a limit (STALLED). */
  attempts: z.number().int().min(0),
  errorCode: provisioningErrorCodeSchema.nullable(),
  waitingReason: provisioningWaitReasonSchema.nullable(),
  counts: z.object({
    categories: z.number().int().min(0),
    products: z.number().int().min(0),
  }),
  /** PENDING or RUNNING with no progress for a while: the worker is probably gone. */
  stalled: z.boolean(),
  /** A platform admin may retry it now (FAILED, or stalled). */
  retryable: z.boolean(),
  startedAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  steps: z.array(provisioningStepStateSchema),
});
export type ProvisioningRun = z.infer<typeof provisioningRunSchema>;

/** `run` is null for tenants that were never provisioned (the master tenant). */
export const tenantProvisioningSchema = z.object({ run: provisioningRunSchema.nullable() });
export type TenantProvisioning = z.infer<typeof tenantProvisioningSchema>;

/** `queued: false` when a job for this tenant is already waiting. */
export const provisioningRetryResponseSchema = z.object({ queued: z.boolean() });

/** The worker job: provision (or resume provisioning) one tenant. */
export const provisioningJobSchema = z.object({ tenantId: z.uuid() });
export type ProvisioningJob = z.infer<typeof provisioningJobSchema>;
